/**
 * VariantSelector — radio list dengan format industrial.
 * [●] 30 DAYS — Rp 50.000
 * [○] LIFETIME — Rp 250.000
 */

import type { ProductListItem } from "@ag5r-store/shared";
import { formatDuration, formatIDR } from "@/lib/format";
import { useLanding } from "@/hooks/useLanding";
import { strikethroughPrice } from "@ag5r-store/shared";

interface Props {
  variants: ProductListItem["variants"];
  value: string | null;
  onChange: (variantId: string) => void;
}

export function VariantSelector({ variants, value, onChange }: Props) {
  // Dipanggil SEBELUM early return di bawah — hook tidak boleh dilewati
  // pada sebagian render.
  const { data: landing } = useLanding();
  const strikeOf = (price: number) =>
    strikethroughPrice(price, landing?.discount_percent ?? 0);

  if (variants.length === 0) {
    return (
      <div className="border border-border p-3 font-mono text-2xs text-ink-muted uppercase">
        no_variants_configured
      </div>
    );
  }

  return (
    <div className="border border-border divide-y divide-border">
      {variants.map((v) => {
        const selected = value === v.id;
        const disabled = v.stock_available !== null && v.stock_available <= 0;
        return (
          <button
            key={v.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v.id)}
            className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-none ${
              selected
                ? "bg-panel border-l-2 border-l-terminal"
                : "hover:bg-panel border-l-2 border-l-transparent"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <span
              className={`font-mono text-sm text-center w-6 shrink-0 ${
                selected ? "text-terminal" : "text-ink-dim"
              }`}
            >
              {selected ? "[●]" : "[○]"}
            </span>
            <span className="flex-1 min-w-0">
              <span className="font-mono text-sm text-ink-primary uppercase tracking-wide">
                {v.name}
              </span>
              <span className="block font-mono text-2xs text-ink-muted mt-0.5">
                {formatDuration(v.duration_days)}
                {v.stock_available !== null && (
                  <> &nbsp;|&nbsp; stock: {v.stock_available}</>
                )}
              </span>
            </span>
            <span className="text-right">
              {/* Harga coret hanya tampilan; yang ditagih tetap v.price. */}
              {strikeOf(v.price) && (
                <span className="block font-sans text-2xs text-ink-dim line-through leading-none">
                  {formatIDR(strikeOf(v.price)!)}
                </span>
              )}
              <span
                className={`font-mono text-sm tabular-nums ${
                  selected ? "text-accent" : "text-ink-primary"
                }`}
              >
                {formatIDR(v.price)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}